/**
 * Importing TJR's staff and aircraft into a Teslyn Cloud register (MB's R78, R79; decision 422 for the staff
 * register; docs/specs/cloud/tjr-register-import.md). The files are read by `tjr-defaults.ts`; this decides what an
 * import adds, matched so nobody and no aircraft is added twice:
 *
 * - **Staff**: by name (`personNameKey`: case and spacing ignored), against every live register row, active or not;
 *   a name of a person deleted from the register (and of no live one) is matched to them and adds nothing (604).
 *   A name on several TJR lists is one person with every role. A matched person only gains the roles TJR gives
 *   that they lack. Only names and roles are imported: TJR's licences, short names and phone numbers stay behind.
 * - **Aircraft**: by registration (upper case; `VH-JOZ` and `vh-joz` are one aircraft). The type comes from a TJR
 *   job that flew the aircraft (its 012 record), else from the national register (CASA) when that register's
 *   aircraft is the one TJR describes (the maximum take-off weights agree), else it is left blank.
 *
 * Nothing is chosen for the person: every row says what would happen and whether it is ticked by default; the screen
 * lets an editor change the ticks before anything is written.
 */
import type { Aircraft, Airframe, Person, RegisterRole } from './model.js';
import {
  REGISTER_ROLES,
  deletedPersonImportNote,
  findDeletedPersonByName,
  findPersonByName,
  normalisePersonName,
  personNameKey,
  personRoles,
} from './staff.js';
import { qcFamilyForAircraft } from './geosoft-qc.js';
import type { RegisterRow } from './aircraft-register.js';
import type { TjrDefaults, TjrJobAircraft, TjrRegistration } from './tjr-defaults.js';
import { tjrTypeName } from './tjr-defaults.js';
import { aircraftPerformanceFromTjr } from './aircraft-performance.js';

// ---------------------------------------------------------------------------------------------- staff

/**
 * `deleted`: the name is a person deleted from the register (decision 604, Q531 B): matched to them, never added
 * again; `note` says a tenant admin or manager can restore them.
 */
export type ImportStatus = 'new' | 'add_roles' | 'add_phone' | 'in_register' | 'deleted';

export interface StaffImportRow {
  /** The name as TJR first writes it, spaces tidied. */
  readonly name: string;
  /** Every role TJR's lists give the name, in register order. */
  readonly roles: readonly RegisterRole[];
  /** TJR's flag: true when any pilot or operator list marks the name `Y`, false when all mark it `N`, null for a
   * name only on the managers list (which has no flag). */
  readonly current: boolean | null;
  readonly status: ImportStatus;
  /** The register person the name matched. */
  readonly personId: string | null;
  /** Roles the matched person does not hold yet (all the roles for a new person). */
  readonly missingRoles: readonly RegisterRole[];
  /** TJR's phone number for the name (decision 137), or null. */
  readonly phone: string | null;
  /** The matched person has no phone number and TJR has one: the import gives it (P02). */
  readonly missingPhone: boolean;
  /**
   * Whether a new person is added active (decision 545): TJR's `Y` (still with Thomson) and managers are active, `N`
   * people inactive, so old records and later TJR imports match them by name but they are not offered for new days.
   * The screen lets the editor change it (decision 546: Ed Dowling, on TJR's managers list, is a former manager).
   */
  readonly active: boolean;
  /** Ticked by default: everything with something to add (decision 545: the inactive people too). */
  readonly selected: boolean;
  /** In words, for a row that adds nothing for a reason (a deleted person, decision 604); '' otherwise. */
  readonly note: string;
}

export interface StaffImportOptions {
  /** Names to add inactive whatever TJR says (decision 546: former managers such as Ed Dowling). */
  readonly inactive?: readonly string[];
}

/** What importing TJR's crew lists into `register` would do, one row per name, by name. */
export function planStaffImport(
  defaults: TjrDefaults,
  register: readonly Person[],
  options: StaffImportOptions = {},
): StaffImportRow[] {
  const inactive = new Set((options.inactive ?? []).map(personNameKey));
  const byKey = new Map<
    string,
    { name: string; roles: Set<RegisterRole>; flags: (boolean | null)[]; phone: string | null }
  >();
  for (const p of defaults.people) {
    const name = normalisePersonName(p.name);
    const key = personNameKey(name);
    const e = byKey.get(key) ?? { name, roles: new Set<RegisterRole>(), flags: [], phone: null };
    e.roles.add(p.role);
    e.flags.push(p.current);
    // the first number TJR's lists give the name
    e.phone ??= p.phone;
    byKey.set(key, e);
  }
  const rows: StaffImportRow[] = [];
  for (const e of byKey.values()) {
    const roles = REGISTER_ROLES.filter((r) => e.roles.has(r));
    const flags = e.flags.filter((f): f is boolean => f !== null);
    const current = flags.length === 0 ? null : flags.some((f) => f);
    const match = findPersonByName(register, e.name);
    const deleted = match ? undefined : findDeletedPersonByName(register, e.name);
    if (deleted) {
      rows.push({
        name: e.name,
        roles,
        current,
        status: 'deleted',
        personId: deleted.id,
        missingRoles: [],
        phone: e.phone,
        missingPhone: false,
        active: false,
        selected: false,
        note: deletedPersonImportNote(deleted.name),
      });
      continue;
    }
    const held = match ? personRoles(match) : [];
    const missingRoles = roles.filter((r) => !held.includes(r));
    // P02 (decision 137): a matched person with no phone number gets TJR's
    const missingPhone = !!match && e.phone !== null && !(match.phone ?? '').trim();
    const status: ImportStatus = !match
      ? 'new'
      : missingRoles.length > 0
        ? 'add_roles'
        : missingPhone
          ? 'add_phone'
          : 'in_register';
    rows.push({
      name: e.name,
      roles,
      current,
      status,
      personId: match?.id ?? null,
      missingRoles,
      phone: e.phone,
      missingPhone,
      active: current !== false && !inactive.has(personNameKey(e.name)),
      selected: status !== 'in_register',
      note: '',
    });
  }
  return rows.sort((a, b) => a.name.localeCompare(b.name, 'en-AU'));
}

/** A register row to insert, or the roles to give a matched person. */
export type StaffImportChange =
  | {
      readonly kind: 'add';
      readonly name: string;
      readonly roles: readonly RegisterRole[];
      readonly active: boolean;
      /** TJR's phone number (decision 137), or null. */
      readonly phone: string | null;
    }
  | {
      readonly kind: 'add_roles';
      readonly personId: string;
      readonly name: string;
      readonly roles: readonly RegisterRole[];
      /** TJR's phone number for a person who has none (P02); absent leaves the phone as it is. */
      readonly phone?: string;
    };

/** The changes of the ticked rows: new people with their roles, matched people with their roles plus TJR's. */
export function staffImportChanges(
  rows: readonly StaffImportRow[],
  register: readonly Person[],
): StaffImportChange[] {
  const out: StaffImportChange[] = [];
  for (const r of rows) {
    if (!r.selected || r.status === 'in_register' || r.status === 'deleted') continue;
    if (r.status === 'new') {
      out.push({ kind: 'add', name: r.name, roles: r.roles, active: r.active, phone: r.phone });
    } else if (r.personId) {
      const p = register.find((x) => x.id === r.personId);
      const held = p ? personRoles(p) : [];
      const roles = REGISTER_ROLES.filter((x) => held.includes(x) || r.missingRoles.includes(x));
      out.push({
        kind: 'add_roles',
        personId: r.personId,
        name: p?.name ?? r.name,
        roles,
        ...(r.missingPhone && r.phone ? { phone: r.phone } : {}),
      });
    }
  }
  return out;
}

// ---------------------------------------------------------------------------------------------- aircraft

/** Where an imported aircraft's type comes from. */
export type AircraftTypeSource = 'tjr_job' | 'register' | 'none';

export interface AircraftImportRow {
  readonly registration: string;
  /** "Cessna Grand Caravan" (a TJR job), "Cessna 210N" (CASA), or '' when unknown. */
  readonly typeName: string;
  readonly typeSource: AircraftTypeSource;
  /** Teslyn's aircraft type (C208, C210, helicopter) when the type says so. */
  readonly airframe: Airframe | null;
  /** Figures from a TJR job's aircraft (copied from TJR's type list): null when no job flew it. */
  readonly fuelFlowLph: number | null;
  readonly mrIntervalH: number | null;
  readonly maxFlightH: number | null;
  readonly fuelType: Aircraft['fuel_type'];
  readonly engines: 1 | 2 | null;
  /** TJR's job safety analysis figures of the registration (tjf.dat 005), kept in `legacy_json.tjr_defaults`. */
  readonly tjr: TjrRegistration | null;
  /** In words, when the register's aircraft under this mark is not the one TJR describes, or other caveats. */
  readonly note: string;
  readonly status: 'new' | 'in_register';
  readonly aircraftId: string | null;
  /**
   * Ticked by default: every aircraft not in the register yet (decision 547: all of them, so old TJR projects match
   * their aircraft), with "Type unknown" where neither a job nor the register gives one, to fill in later.
   */
  readonly selected: boolean;
}

/**
 * The register's rows Teslyn looks at (what `aircraft_register_lookup` answers), with `names_tenant` when the caller
 * compared its holder and operator with the organisation (`registerNamesTenant`).
 */
export type RegisterTypeRow = Pick<
  RegisterRow,
  'mark' | 'manufacturer' | 'model' | 'icao_type' | 'airframe' | 'mtow_kg'
> & {
  readonly names_tenant?: boolean;
};

/**
 * The register names the organisation as the aircraft's holder or operator: the first word of the organisation's
 * name ("Thomson" of "Thomson Airborne") begins a word of either party's name ("Thomson Airborne Pty Ltd").
 */
export function registerNamesTenant(
  row: Pick<RegisterRow, 'holder' | 'operator'>,
  tenantName: string,
): boolean {
  const first = tenantName.trim().split(/\s+/)[0]?.toLowerCase() ?? '';
  if (first.length < 3) return false;
  return [row.holder?.name, row.operator?.name].some((n) =>
    (n ?? '')
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .includes(first),
  );
}

/** The key two registrations are compared by. */
export function registrationKey(reg: string): string {
  return reg.trim().toUpperCase().replace(/\s+/g, '');
}

/** Teslyn's aircraft type for a register row (its ICAO type designator or its airframe). */
function airframeOfRegister(r: RegisterTypeRow): Airframe | null {
  const t = (r.icao_type ?? '').toUpperCase();
  if (t === 'C208') return 'c208';
  if (/^[CPT]210$/.test(t)) return 'c210';
  if (r.airframe === 'helicopter') return 'heli';
  return null;
}

/** The register's maximum take-off weight agrees with TJR's for the mark (within 10 %): the same aircraft. */
function sameAircraft(tjr: TjrRegistration | null, r: RegisterTypeRow): boolean {
  if (!tjr?.mtowKg || !r.mtow_kg) return true;
  return Math.abs(r.mtow_kg - tjr.mtowKg) <= 0.1 * tjr.mtowKg;
}

/**
 * What importing TJR's aircraft into `register` would do: every registration of `tjf.dat` (005) and of the jobs
 * given (012), by registration. `lookup` is the national register's answer for those marks (may be empty).
 */
export function planAircraftImport(
  defaults: TjrDefaults | null,
  jobs: readonly TjrJobAircraft[],
  register: readonly Pick<Aircraft, 'id' | 'registration' | 'deleted_at'>[],
  lookup: readonly RegisterTypeRow[] = [],
): AircraftImportRow[] {
  const keys = new Map<string, string>();
  for (const r of defaults?.registrations ?? []) keys.set(registrationKey(r.registration), r.registration);
  for (const j of jobs)
    if (!keys.has(registrationKey(j.registration))) keys.set(registrationKey(j.registration), j.registration);
  const rows: AircraftImportRow[] = [];
  for (const [key, registration] of keys) {
    const tjr = defaults?.registrations.find((r) => registrationKey(r.registration) === key) ?? null;
    const job = jobs.find((j) => registrationKey(j.registration) === key) ?? null;
    const reg = lookup.find((r) => registrationKey(r.mark) === key) ?? null;
    let typeName = '';
    let typeSource: AircraftTypeSource = 'none';
    let airframe: Airframe | null = null;
    let note = '';
    if (job && tjrTypeName(job)) {
      typeName = tjrTypeName(job);
      typeSource = 'tjr_job';
      airframe = qcFamilyForAircraft(typeName) ?? (job.helicopter ? 'heli' : null);
    } else if (reg && sameAircraft(tjr, reg)) {
      typeName = [reg.manufacturer, reg.model].filter(Boolean).join(' ');
      typeSource = typeName ? 'register' : 'none';
      airframe = airframeOfRegister(reg);
    } else if (reg) {
      const what = [reg.manufacturer, reg.model].filter(Boolean).join(' ');
      note = `The register's ${registration} is now another aircraft (${what ? `${what}, ` : ''}${reg.mtow_kg} kg; the list's weighs ${tjr?.mtowKg} kg): type left blank.`;
    }
    if (typeSource === 'none' && !note) note = 'No project file or register entry gives its type.';
    const otherOwner = typeSource !== 'tjr_job' && reg?.names_tenant === false;
    if (otherOwner && !note) note = 'The register names another holder and operator.';
    const match =
      register.find((a) => a.deleted_at === null && registrationKey(a.registration) === key) ?? null;
    const engines = job?.engines === 1 || job?.engines === 2 ? job.engines : null;
    rows.push({
      registration,
      typeName,
      typeSource,
      airframe,
      fuelFlowLph: job?.fuelFlowLph ?? null,
      mrIntervalH: job?.mrIntervalH ?? null,
      maxFlightH: job?.maxFlightH ?? null,
      fuelType: job?.fuelType ?? null,
      engines,
      tjr,
      note,
      status: match ? 'in_register' : 'new',
      aircraftId: match?.id ?? null,
      selected: !match,
    });
  }
  return rows.sort((a, b) => a.registration.localeCompare(b.registration, 'en-AU'));
}

/** The `aircraft` row a ticked new aircraft is inserted as (the database fills id and audit columns). */
export interface AircraftImportInsert {
  readonly tenant_id: string;
  readonly registration: string;
  readonly type_name: string;
  readonly airframe: Airframe | null;
  readonly fuel_flow_lph: number | null;
  readonly mr_interval_h: number | null;
  readonly max_flight_h: number | null;
  readonly fuel_type: Aircraft['fuel_type'];
  readonly engines: 1 | 2 | null;
  /** TJR's 005 BEW, MTOW and reserve, kept in the register for the JSA (decision 863, 6 B; aircraft-performance.ts). */
  readonly empty_weight_kg: number | null;
  readonly mtow_kg: number | null;
  readonly reserve_min: number | null;
  readonly legacy_json: Readonly<Record<string, unknown>>;
}

/** TJR's 005 figures as the register keeps them (the same checks as `aircraftPerformanceFromTjr`). */
function tjrPerformance(
  t: AircraftImportRow['tjr'],
): Pick<AircraftImportInsert, 'empty_weight_kg' | 'mtow_kg' | 'reserve_min'> {
  return aircraftPerformanceFromTjr({
    legacy_json: t ? { tjr_defaults: { bew_kg: t.bewKg, mtow_kg: t.mtowKg, reserve_min: t.reserveMin } } : {},
  });
}

export function aircraftImportInserts(
  rows: readonly AircraftImportRow[],
  tenantId: string,
): AircraftImportInsert[] {
  return rows
    .filter((r) => r.selected && r.status === 'new')
    .map((r) => ({
      tenant_id: tenantId,
      registration: r.registration,
      type_name: r.typeName,
      airframe: r.airframe,
      fuel_flow_lph: r.fuelFlowLph,
      mr_interval_h: r.mrIntervalH,
      max_flight_h: r.maxFlightH,
      fuel_type: r.fuelType ?? null,
      engines: r.engines,
      ...tjrPerformance(r.tjr),
      legacy_json: {
        imported_from: 'tjr',
        type_source: r.typeSource,
        ...(r.tjr
          ? {
              tjr_defaults: {
                bew_kg: r.tjr.bewKg,
                mtow_kg: r.tjr.mtowKg,
                max_fuel_l: r.tjr.maxFuelL,
                fuel_lph: r.tjr.fuelLph,
                airspeed_kts: r.tjr.airspeedKts,
                reserve_min: r.tjr.reserveMin,
                tip_tanks: r.tjr.tipTanks,
              },
            }
          : {}),
      },
    }));
}
